import { Email } from "@mui/icons-material";
import { Box, Grid, IconButton, Typography } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import useMSSP from "../../../api/hooks/useMSSP";
import useSmartAndRegularAssets from "../../../api/hooks/useSmartAndRegularAssets";
import { controlKeys, vulnerabilityKeys } from "../../../api/queryKeys";
import ExportButton from "../../../components/buttons/ExportButton";
import CustomToolTip from "../../../components/CustomTooltip";
import ImgOption from "../../../components/filterOption/SeverityOption";
import AndOrFilter from "../../../components/filters/AndOrFilter";
import ClearFiltersButton from "../../../components/filters/ClearFiltersButton";
import FilterMenu from "../../../components/filters/FilterMenu";
import RangeSliderFilter from "../../../components/filters/RangeSliderFilter";
import SearchAndOrFilter from "../../../components/filters/SearchAndOrFilter";
import SearchInput from "../../../components/filters/SearchInput";
import LoadingIcon from "../../../components/LoadingIcon";
import TenantAvatar from "../../../components/mssp/TenantAvatar";
import AppPage from "../../../components/structure/AppPage";
import { Toast } from "../../../components/Toast";
import { BootstrapContext } from "../../../contexts/BootstrapContext";
import { EnvFeatureFlagContext } from "../../../contexts/EnvFeaturesContext";
import { useFileNaming } from "../../../hooks/useFileNaming";
import { VulnerabilityTypeChip } from "../../../utils/chipsData";
import { LogicNameFilter } from "../../../utils/constants";
import { searchObjectProperties } from "../../../utils/search";
import { daysPastSince } from "../../../utils/timestamp";
import { IntegrationsMapping } from "../../stack/integrations/IntegrationsMapping";
import IntegrationRowFilter from "../steps/components/IntegrationRowFilter";
import ExportVulnerabilitiesModal from "./components/ExportVulnerabilitiesModal";
import {
  TargetTypesRowFilter,
  VulnerabilityTargetType,
  VulnerabilityTargetTypeToDisplayName,
} from "./components/TargetTypes";
import { topExposureDaysCalculate } from "./utils";
import VulnerabilitiesTable from "./VulnerabilitiesTable";
import VulnerabilityDrawer from "./vulnerabilityDrawer/VulnerabilityDrawer";

// Matches the values in the backend/constants.py VulnerabilityExploit enum
export const VulnerabilityExploit = {
  AVAILABLE: "available",
  NOT_AVAILABLE: "not_available",
  HAS_EXPLOIT_IN_THE_WILD: "has_exploit_in_the_wild",
};

const VulnerabilitiesView = () => {
  const colors = useColors();
  const { generateFileName } = useFileNaming();
  const {
    fetchVulnerabilities,
    fetchAllControls,
    trackEvent,
    exportVulnerabilitiesAssociations,
  } = useAPI();
  const gridRef = useGridApiRef();
  const { isActiveRds } = useContext(EnvFeatureFlagContext);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [maxNumberOfDaysFound, setMaxNumberOfDaysFound] = useState(0);
  const [filterLogic, setFilterLogic] = useState({
    affectedAssets: LogicNameFilter.LOGIC_AND,
    sources: LogicNameFilter.LOGIC_AND,
    compensatingTools: LogicNameFilter.LOGIC_AND,
    targetTypes: LogicNameFilter.LOGIC_AND,
  });
  const { selectedTenantIds, hasMultipleSelectedTenants, currentTenantId } =
    useMSSP();
  const [appliedFilters, setAppliedFilters] = useState({
    searchString: "",
    vulnerabilityIds: [],
    sources: {},
    locationSource: {},
    severity: {},
    targetTypes: {},
    minNumber: 0,
    hasFix: {},
    hasExploit: {},
    daysFound: {
      min: 0,
      max: 0,
    },
    score: {
      min: 0,
      max: 0,
    },
    entityCount: {
      min: 0,
      max: 0,
    },
    compensatingTools: {},
    affectedAssets: {},
    tenant: {},
  });

  const [appliedFiltersDays, setAppliedFiltersDays] = useState({
    minNumber: 0,
    maxNumber: maxNumberOfDaysFound,
  });
  const [maxNumberScore, setMaxNumberScore] = useState(0);
  const [maxEntityCount, setMaxEntityCount] = useState(0);

  const [topExposureDays, setTopExposureDays] = useState(0);
  const [filteredVulnerabilities, setFilteredVulnerabilities] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    sources: [],
    locationSource: [],
    severity: ["Critical", "High", "Medium", "Low"],
    targetTypes: [
      "All",
      VulnerabilityTargetType.ENDPOINT,
      VulnerabilityTargetType.SERVER,
      VulnerabilityTargetType.CONTAINER,
      VulnerabilityTargetType.SERVERLESS,
      VulnerabilityTargetType.DATASTORE,
    ],
    hasFix: ["All", "Yes", "No"],
    hasExploit: [
      "All",
      VulnerabilityExploit.AVAILABLE,
      VulnerabilityExploit.NOT_AVAILABLE,
      VulnerabilityExploit.HAS_EXPLOIT_IN_THE_WILD,
    ],
    hasCompensatingControls: ["Yes", "No"],
    daysFound: [],
    compensatingTools: [],
    affectedAssets: [],
  });
  const [openVulnerabilitiesDrawer, setOpenVulnerabilitiesDrawer] =
    useState(false);
  const [selectedVulnerability, setSelectedVulnerability] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { msspTenants } = useContext(BootstrapContext);

  const location = useLocation();

  const exportMutation = useMutation({
    mutationFn: ({
      vulnerabilityIds,
      tenantIds,
      vulnerabilityFields,
      entityFields,
    }) =>
      exportVulnerabilitiesAssociations(
        vulnerabilityIds,
        tenantIds,
        vulnerabilityFields,
        entityFields
      ),
    onSuccess: () => {
      Toast.info(
        "Your report is being prepared and will be delivered to your email within the next hour."
      );
    },
    onError: () => {
      Toast.error("Failed to initiate vulnerabilities export.");
    },
  });

  const handleExportFindingsClick = () => {
    trackEvent('User click on "Export Findings" button');
    setExportModalOpen(true);
  };

  const handleExportFindingsConfirm = (
    selectedTenantIds,
    selectedVulnerabilityFields,
    selectedEntityFields
  ) => {
    const vulnerabilityIds = filteredVulnerabilities.map(
      (v) => v.vulnerability_id
    );
    exportMutation.mutate({
      vulnerabilityIds,
      tenantIds: selectedTenantIds,
      vulnerabilityFields: selectedVulnerabilityFields,
      entityFields: selectedEntityFields,
    });
    setExportModalOpen(false);
  };

  const handleTableExport = () => {
    trackEvent(
      'User click on "Export" button to download the "vulnerabilities" table'
    );
    gridRef?.current?.exportDataAsCsv({
      fileName: generateFileName("vulnerabilities"),
    });
  };

  const {
    data: vulnerabilities,
    isLoading: vulnerabilitiesAreLoading,
    isFetching: vulnerabilitiesAreFetching,
  } = useQuery({
    queryKey: vulnerabilityKeys.all,
    queryFn: async () => {
      return await fetchVulnerabilities();
    },
    select: (response) => response.data,
  });

  const customerIds = Array.from(
    new Set(
      vulnerabilities?.flatMap((vulnerability) => vulnerability.customer_ids)
    )
  );

  const tenants = useMemo(() => {
    if (!msspTenants?.length) return [];
    return [...msspTenants].sort((a, b) => {
      if (a.customer_id === currentTenantId) return -1;
      if (b.customer_id === currentTenantId) return 1;
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;
      return 0;
    });
  }, [msspTenants, currentTenantId]);

  const { regularAndActiveSmartAsset: assets, isLoadingAll: assetsAreLoading } =
    useSmartAndRegularAssets(false);

  const { data: controls, isLoading: controlsAreLoading } = useQuery({
    queryKey: controlKeys.all,
    queryFn: fetchAllControls,

    select: (data) =>
      data.data.map((control) => ({
        ...control,
        id: control.control_id,
      })),
  });

  const dataIsLoading =
    vulnerabilitiesAreLoading || assetsAreLoading || controlsAreLoading;

  useEffect(() => {
    if (vulnerabilities) {
      setTopExposureDays(topExposureDaysCalculate(vulnerabilities));

      const daysFound = vulnerabilities?.map((row) => {
        if (row?.published_date?.timestamp_utc_human_readable) {
          return daysPastSince(
            row?.published_date?.timestamp_utc_human_readable
          );
        } else {
          return 0;
        }
      });
      const scores = vulnerabilities
        ?.map((row) => {
          return row.breeze_score?.total;
        })
        .filter((score) => score !== undefined);

      const localMaxScore = Math.max(...scores);
      setMaxNumberScore(localMaxScore ? Number(localMaxScore.toFixed(1)) : 0);

      const entityCounts = vulnerabilities?.map((row) => {
        return row.entity_count || 0;
      });
      const localMaxEntityCount = Math.max(...entityCounts);
      setMaxEntityCount(localMaxEntityCount ? localMaxEntityCount : 0);

      const localMaxDays = Math.max(...daysFound);
      setMaxNumberOfDaysFound(localMaxDays ? localMaxDays : 0);
      setAppliedFilters((prev) => ({
        ...prev,
        daysFound: { min: 0, max: localMaxDays },
        score: { min: 0, max: localMaxScore },
        entityCount: { min: 0, max: localMaxEntityCount },
      }));
    }
    // JSON.stringify is used to compare objects and arrays as dependencies
    // useEffect checks objects and arrays by reference, so we need to compare them by value
  }, [JSON.stringify(vulnerabilities)]);

  useEffect(() => {
    if (assets && vulnerabilities && !dataIsLoading) {
      setFilteredVulnerabilities(vulnerabilities);

      setFilterOptions((prev) => ({
        ...prev,
        affectedAssets: ["All", ...assets?.map((asset) => asset.name).sort()],
      }));

      let sources = vulnerabilities
        .map((vulnerability) => vulnerability.sources)
        .flat()
        .filter((source) => typeof source == "string");
      sources = [...new Set(sources)].sort();
      setFilterOptions((prev) => ({ ...prev, sources: ["All", ...sources] }));

      let locationSources = vulnerabilities
        .map((vulnerability) => vulnerability.location_source)
        .filter(
          (locationSource) =>
            locationSource && typeof locationSource === "string"
        );
      locationSources = [...new Set(locationSources)].sort();
      setFilterOptions((prev) => ({
        ...prev,
        locationSource: ["All", ...locationSources],
      }));

      let tools = controls.map((control) => control.tool);
      tools = [...new Set(tools)].sort();
      setFilterOptions((prev) => ({
        ...prev,
        compensatingTools: tools,
      }));
    }
  }, [assets, vulnerabilities, dataIsLoading]);

  //filter the vulnerabilities based on the applied filters
  useEffect(() => {
    if (vulnerabilities) {
      let newFilters = { ...appliedFilters };
      let message =
        "User applied filters on vulnerabilities: " +
        JSON.stringify(newFilters);
      trackEvent(message).then();

      let filtered = vulnerabilities;

      if (Object.keys(appliedFilters.sources).length > 0) {
        const sources_filter = Object.keys(appliedFilters.sources).filter(
          (key) => appliedFilters.sources[key] === true
        );
        if (sources_filter.length > 0) {
          if (filterLogic["sources"] === LogicNameFilter.LOGIC_OR) {
            filtered = filtered.filter(
              (vulnerability) =>
                vulnerability?.sources &&
                vulnerability?.sources.some((source) =>
                  sources_filter.includes(source)
                )
            );
          } else if (filterLogic["sources"] === LogicNameFilter.LOGIC_AND) {
            filtered = filtered.filter(
              (vulnerability) =>
                vulnerability?.sources &&
                sources_filter.every((source) => {
                  if (source !== "All") {
                    return vulnerability?.sources.includes(source);
                  }
                  return true;
                })
            );
          }
        }
      }
      if (Object.keys(appliedFilters.locationSource).length > 0) {
        const locationSource_filter = Object.keys(
          appliedFilters.locationSource
        ).filter((key) => appliedFilters.locationSource[key] === true);
        if (locationSource_filter.length > 0) {
          filtered = filtered.filter((vulnerability) => {
            if (!vulnerability?.location_source) return false;
            return locationSource_filter.some((source) => {
              if (source === "All") return true;
              return vulnerability.location_source === source;
            });
          });
        }
      }
      if (Object.keys(appliedFilters.severity).length > 0) {
        const severity_filter = Object.keys(appliedFilters.severity).filter(
          (key) => appliedFilters.severity[key] === true
        );
        if (severity_filter.length > 0) {
          filtered = filtered.filter((vulnerability) =>
            severity_filter.includes(
              VulnerabilityTypeChip.vulnerabilitySeverity[
                vulnerability?.severity
              ]?.label
            )
          );
        }
      }

      if (Object.keys(appliedFilters.targetTypes).length > 0) {
        const targetTypes_filter = Object.keys(
          appliedFilters.targetTypes
        ).filter((key) => appliedFilters.targetTypes[key] === true);
        if (targetTypes_filter.length > 0) {
          if (filterLogic["targetTypes"] === LogicNameFilter.LOGIC_OR) {
            filtered = filtered.filter(
              (vulnerability) =>
                vulnerability?.target_types &&
                vulnerability?.target_types.some((target_type) =>
                  targetTypes_filter.includes(target_type)
                )
            );
          } else if (filterLogic["targetTypes"] === LogicNameFilter.LOGIC_AND) {
            filtered = filtered.filter(
              (vulnerability) =>
                vulnerability?.target_types &&
                targetTypes_filter.every((target_type) => {
                  if (target_type !== "All") {
                    return vulnerability?.target_types.includes(target_type);
                  }
                  return true;
                })
            );
          }
        }
      }
      if (Object.keys(appliedFilters.hasExploit).length > 0) {
        const hasExploit_filter = Object.keys(appliedFilters.hasExploit).filter(
          (key) => appliedFilters.hasExploit[key] === true
        );
        if (hasExploit_filter.length > 0) {
          filtered = filtered.filter((vulnerability) => {
            let exploit = vulnerability.exploit;
            return hasExploit_filter.includes(exploit);
          });
        }
      }
      if (Object.keys(appliedFilters.hasFix).length > 0) {
        const hasFix_filter = Object.keys(appliedFilters.hasFix).filter(
          (key) => appliedFilters.hasFix[key] === true
        );
        if (hasFix_filter.length > 0) {
          filtered = filtered.filter((vulnerability) => {
            const hasFix = vulnerability.has_fix ? "Yes" : "No";
            return hasFix_filter.includes(hasFix);
          });
        }
      }
      if (Object.keys(appliedFilters.compensatingTools).length > 0) {
        const compensatingTools_filter = Object.keys(
          appliedFilters.compensatingTools
        ).filter((key) => appliedFilters.compensatingTools[key] === true);
        if (compensatingTools_filter.length > 0) {
          if (filterLogic["compensatingTools"] === LogicNameFilter.LOGIC_OR) {
            filtered = filtered.filter((vulnerability) => {
              const compensatedControls = controls.filter((control) =>
                vulnerability.compensating_control_ids.includes(
                  control.control_id
                )
              );
              const compensatedTools = compensatedControls.map(
                (control) => control.tool
              );
              if (
                compensatedTools.some((tool) =>
                  compensatingTools_filter.includes(tool)
                )
              )
                return true;
              return false;
            });
          } else if (
            filterLogic["compensatingTools"] === LogicNameFilter.LOGIC_AND
          ) {
            filtered = filtered.filter((vulnerability) => {
              const compensatedControls = controls.filter((control) =>
                vulnerability.compensating_control_ids.includes(
                  control.control_id
                )
              );
              const compensatedTools = compensatedControls.map(
                (control) => control.tool
              );
              if (
                compensatingTools_filter.every((tool) =>
                  compensatedTools.includes(tool)
                )
              )
                return true;
              return false;
            });
          }
        }
      }
      if (Object.keys(appliedFilters.tenant).length > 0) {
        const tenant_filter = Object.keys(appliedFilters.tenant).filter(
          (key) => appliedFilters.tenant[key] === true
        );
        if (tenant_filter.length > 0) {
          filtered = filtered.filter((vulnerability) =>
            vulnerability.customer_ids.some((customer_id) =>
              tenant_filter.includes(customer_id)
            )
          );
        }
      }
      if (Object.keys(appliedFilters.affectedAssets).length > 0) {
        const affectedAssets_filter = Object.keys(
          appliedFilters.affectedAssets
        ).filter((key) => appliedFilters.affectedAssets[key] === true);
        if (affectedAssets_filter.length > 0) {
          const selectedAssetIDs = assets
            ?.filter((asset) => affectedAssets_filter.includes(asset.name))
            .map((asset) => asset.asset_id);
          if (filterLogic["affectedAssets"] === LogicNameFilter.LOGIC_OR) {
            filtered = filtered.filter((vulnerability) =>
              vulnerability.related_asset_ids.some((asset_id) =>
                selectedAssetIDs.includes(asset_id)
              )
            );
          } else if (
            filterLogic["affectedAssets"] === LogicNameFilter.LOGIC_AND
          ) {
            filtered = filtered.filter((vulnerability) =>
              selectedAssetIDs.every((asset_id) => {
                return vulnerability.related_asset_ids.includes(asset_id);
              })
            );
          }
        }
      }

      if (
        appliedFilters.score.min > 0 ||
        appliedFilters.score.max < maxNumberScore
      ) {
        filtered = filtered.filter((vulnerability) => {
          return (
            vulnerability.breeze_score?.total >= appliedFilters.score.min &&
            vulnerability.breeze_score?.total <= appliedFilters.score.max
          );
        });
      }

      if (
        appliedFilters.entityCount.min > 0 ||
        appliedFilters.entityCount.max < maxEntityCount
      ) {
        filtered = filtered.filter((vulnerability) => {
          const entityCount = vulnerability.entity_count || 0;
          return (
            entityCount >= appliedFilters.entityCount.min &&
            entityCount <= appliedFilters.entityCount.max
          );
        });
      }

      if (
        appliedFilters.daysFound.min > 0 ||
        appliedFilters.daysFound.max < maxNumberOfDaysFound
      ) {
        filtered = filtered.filter((vulnerability) => {
          const daysPast = daysPastSince(
            vulnerability?.published_date?.timestamp_utc_human_readable
          );
          return (
            daysPast >= appliedFilters.daysFound.min &&
            daysPast <= appliedFilters.daysFound.max
          );
        });
      }
      if (appliedFilters.searchString !== "") {
        const lowerCaseSearchString = appliedFilters.searchString.toLowerCase();
        filtered = filtered.filter((row) =>
          searchObjectProperties(row, lowerCaseSearchString)
        );
      }
      if (appliedFilters.vulnerabilityIds.length > 0) {
        const vulnerabilityIds = appliedFilters.vulnerabilityIds;
        filtered = filtered.filter((row) =>
          vulnerabilityIds.includes(row.vulnerability_id)
        );
      }

      setFilteredVulnerabilities(filtered);
    }
  }, [
    vulnerabilities,
    appliedFilters.sources,
    appliedFilters.locationSource,
    appliedFilters.severity,
    appliedFilters.targetTypes,
    appliedFilters.searchString,
    appliedFilters.hasExploit,
    appliedFilters.hasFix,
    appliedFilters.minNumber,
    appliedFiltersDays.minNumber,
    appliedFilters.compensatingTools,
    appliedFilters.affectedAssets,
    appliedFilters.daysFound,
    appliedFilters.score,
    appliedFilters.entityCount,
    appliedFilters.tenant,
    filterLogic,
  ]);

  useEffect(() => {
    if (searchParams.has("cve") && vulnerabilities) {
      const cve = searchParams.get("cve");
      const vulnerability = vulnerabilities.find(
        (vulnerability) => vulnerability.vulnerability_id === cve
      );
      if (vulnerability) {
        setSelectedVulnerability(vulnerability);
        setOpenVulnerabilitiesDrawer(true);
      }
    }
  }, [vulnerabilities, location]);

  const handleVulnerabilityClick = (vulnerability) => {
    searchParams.set("cve", vulnerability.vulnerability_id);
    setSearchParams(searchParams.toString());
  };

  const handleRowClick = (row) => {
    const vulnerability = row.row;
    setSelectedVulnerability(vulnerability);
    setOpenVulnerabilitiesDrawer(true);
    trackEvent(
      `Click on vulnerability row with ID ${vulnerability?.vulnerability_id}`
    );
    handleVulnerabilityClick(vulnerability);
  };

  const handleOnClose = () => {
    setSearchParams({});
    setOpenVulnerabilitiesDrawer(false);
    setSelectedVulnerability(null);
    trackEvent("User close Vulnerability Drawer");
  };

  return (
    <>
      {dataIsLoading ? (
        <LoadingIcon />
      ) : (
        <AppPage title="Vulnerabilities">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Grid container direction={"column"} gap={2}>
              <Grid container width={"100%"} gap={1} alignItems={"center"}>
                {/* SCORE FILTER */}
                <Grid item>
                  <Box display={"flex"} flexDirection={"row"} gap={1}>
                    <RangeSliderFilter
                      nameField={"score"}
                      title={"Score"}
                      max={maxNumberScore}
                      min={0}
                      setAppliedFilters={setAppliedFilters}
                      step={0.05}
                      appliedFilters={appliedFilters}
                    />
                  </Box>
                </Grid>
                {/* ENTITY COUNT FILTER */}
                <Grid item>
                  <Box display={"flex"} flexDirection={"row"} gap={1}>
                    <RangeSliderFilter
                      nameField={"entityCount"}
                      title={"Affected Entities"}
                      max={maxEntityCount}
                      min={0}
                      setAppliedFilters={setAppliedFilters}
                      step={1}
                      appliedFilters={appliedFilters}
                    />
                  </Box>
                </Grid>
                {/* INTEGRATION FILTER (formerly SOURCES) */}
                <Grid item>
                  <SearchAndOrFilter
                    filterName={"Integration"}
                    fieldName={"sources"}
                    options={filterOptions.sources.map((source) => {
                      if (source === "All") {
                        return {
                          name: source,
                          label: (
                            <Typography variant="caption">All Tools</Typography>
                          ),
                          displayName: source,
                        };
                      }
                      return {
                        name: source,
                        label: <IntegrationRowFilter name={source} />,
                        displayName: IntegrationsMapping[source].displayName,
                      };
                    })}
                    appliedFilters={appliedFilters}
                    setAppliedFilters={setAppliedFilters}
                    setFilterLogic={setFilterLogic}
                    filterLogic={filterLogic.sources}
                    fullOptions={filterOptions.sources}
                  />
                </Grid>
                {/* SOURCE FILTER (location_source) */}
                <Grid item>
                  <FilterMenu
                    filterName={"Source"}
                    fieldName={"locationSource"}
                    options={filterOptions.locationSource.map((source) => {
                      return {
                        name: source,
                        label: (
                          <Typography variant="caption">
                            {source === "All" ? "All Sources" : source}
                          </Typography>
                        ),
                        displayName: source,
                      };
                    })}
                    appliedFilters={appliedFilters}
                    setAppliedFilters={setAppliedFilters}
                    fullOptions={filterOptions.locationSource}
                    showSearch={true}
                    displayAs="chip"
                  />
                </Grid>
                {/* SEVERITY FILTER */}
                <Grid item>
                  <Box display={"flex"} flexDirection={"row"} gap={1}>
                    <FilterMenu
                      filterName={"Severity"}
                      fieldName={"severity"}
                      options={filterOptions.severity.map((severity) => {
                        return {
                          name: severity,
                          label: (
                            <ImgOption type={"severity"} label={severity} />
                          ),
                          displayName: severity,
                          disabled: ["Low"].includes(severity),
                        };
                      })}
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                      fullOptions={filterOptions.severity}
                      displayAs="chip"
                    />
                  </Box>
                </Grid>
                {/* TARGETS FILTER */}
                <Grid item>
                  <Box display={"flex"} flexDirection={"row"} gap={1}>
                    <AndOrFilter
                      filterName={"Target"}
                      fieldName={"targetTypes"}
                      options={filterOptions.targetTypes.map((targetType) => {
                        return {
                          name: targetType,
                          label:
                            targetType === "All" ? (
                              <Typography variant="caption">All</Typography>
                            ) : (
                              <TargetTypesRowFilter targetType={targetType} />
                            ),
                          displayName:
                            VulnerabilityTargetTypeToDisplayName[targetType],
                        };
                      })}
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                      setFilterLogic={setFilterLogic}
                      fullOptions={filterOptions.targetTypes}
                      displayAs="chip"
                    />
                  </Box>
                </Grid>
                {/* HAS FIX FILTER */}
                <Grid item>
                  <Box display={"flex"} flexDirection={"row"} gap={1}>
                    <FilterMenu
                      filterName={"Fix"}
                      fieldName={"hasFix"}
                      options={[
                        {
                          name: "All",
                          label: "All statuses",
                          displayName: "All Statuses",
                        },
                        {
                          name: "Yes",
                          label: "Available",
                          displayName: "Available",
                        },
                        {
                          name: "No",
                          label: "Not Available",
                          displayName: "Not Available",
                        },
                      ]}
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                      displayAs="chip"
                    />
                  </Box>
                </Grid>
                {/* HAS EXPLOIT FILTER */}
                <Grid item>
                  <Box display={"flex"} flexDirection={"row"} gap={1}>
                    <FilterMenu
                      filterName={"Exploit"}
                      fieldName={"hasExploit"}
                      options={[
                        {
                          name: "All",
                          label: "All statuses",
                          displayName: "All Statuses",
                        },
                        {
                          name: VulnerabilityExploit.AVAILABLE,
                          label: "Available",
                          displayName: "Available",
                        },
                        {
                          name: VulnerabilityExploit.NOT_AVAILABLE,
                          label: "Not Available",
                          displayName: "Not Available",
                        },
                        {
                          name: VulnerabilityExploit.HAS_EXPLOIT_IN_THE_WILD,
                          label: "Has Exploit in the Wild",
                          displayName: "Has Exploit in the Wild",
                        },
                      ]}
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                      displayAs="chip"
                    />
                  </Box>
                </Grid>
                {/* DAYS FOUND FILTER */}
                <Grid item>
                  <Box display={"flex"} flexDirection={"row"} gap={1}>
                    <RangeSliderFilter
                      nameField={"daysFound"}
                      title={"Exposure Days"}
                      max={maxNumberOfDaysFound}
                      min={0}
                      setAppliedFilters={setAppliedFilters}
                      appliedFilters={appliedFilters}
                    />
                  </Box>
                </Grid>
                {/* TENANT FILTER-ONLY FOR MSSP MULTI-TENANT VIEW */}
                {hasMultipleSelectedTenants && tenants?.length > 0 && (
                  <Grid item>
                    <Box display={"flex"} flexDirection={"row"} gap={1}>
                      <FilterMenu
                        filterName={"Tenant"}
                        fieldName={"tenant"}
                        options={customerIds.map((tenant_id) => {
                          const tenant = tenants?.find(
                            (tenant) => tenant?.customer_id === tenant_id
                          );
                          return {
                            name: tenant_id,
                            label: (
                              <Box
                                display={"flex"}
                                flexDirection={"row"}
                                gap={1}
                                alignItems={"center"}
                              >
                                <TenantAvatar tenant={tenant} />
                                <Typography variant="caption">
                                  {tenant?.company_name}
                                </Typography>
                              </Box>
                            ),
                            displayName: tenant_id,
                          };
                        })}
                        appliedFilters={appliedFilters}
                        setAppliedFilters={setAppliedFilters}
                        displayAs="chip"
                      />
                    </Box>
                  </Grid>
                )}
                {/* COMPENSATING CONTROLS FILTER */}
                {!hasMultipleSelectedTenants && (
                  <Grid item>
                    <SearchAndOrFilter
                      filterName={"Compensating Controls"}
                      fieldName={"compensatingTools"}
                      options={filterOptions.compensatingTools.map((tool) => {
                        return {
                          name: tool,
                          label: <IntegrationRowFilter name={tool} />,
                          displayName: IntegrationsMapping[tool].displayName,
                        };
                      })}
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                      setFilterLogic={setFilterLogic}
                      filterLogic={filterLogic.compensatingTools}
                      fullOptions={filterOptions.compensatingTools}
                    />
                  </Grid>
                )}
                {/* AFFECTED ASSETS FILTER */}
                {!hasMultipleSelectedTenants && (
                  <Grid item>
                    <Box display={"flex"} flexDirection={"row"} gap={1}>
                      <SearchAndOrFilter
                        filterName={"Affected Assets"}
                        fieldName={"affectedAssets"}
                        options={filterOptions.affectedAssets.map((asset) => {
                          if (asset === "All") {
                            return {
                              name: asset,
                              label: (
                                <Typography variant="caption">
                                  All Critical Assets
                                </Typography>
                              ),
                              displayName: asset,
                            };
                          }
                          return {
                            name: asset,
                            label: asset,
                            displayName: asset,
                          };
                        })}
                        appliedFilters={appliedFilters}
                        setAppliedFilters={setAppliedFilters}
                        fullOptions={filterOptions.affectedAssets}
                        setFilterLogic={setFilterLogic}
                        filterLogic={filterLogic.affectedAssets}
                        displayAs="chip"
                      />
                    </Box>
                  </Grid>
                )}
                <Grid item>
                  <ClearFiltersButton
                    appliedFilters={appliedFilters}
                    setAppliedFilters={setAppliedFilters}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid container justifyContent="space-between">
              <Grid item xs={3}>
                <SearchInput
                  fieldName={"searchString"}
                  appliedFilters={appliedFilters}
                  setAppliedFilters={setAppliedFilters}
                />
              </Grid>
              <Grid item display="flex" gap={1}>
                {isActiveRds && (
                  <CustomToolTip
                    title="Export findings to email"
                    placement="top"
                  >
                    <IconButton
                      sx={{
                        background: colors.common_white,
                        color: colors.action_active,
                        height: "32px",
                        width: "32px",
                        border: "1px solid " + colors.grey_48percent,
                        borderRadius: "8px",
                        "&:hover": {
                          background: colors.grey_8percent,
                        },
                      }}
                      onClick={handleExportFindingsClick}
                      disabled={exportMutation.isPending}
                    >
                      <Email
                        sx={{ fontSize: 18, color: colors.icon_action_active }}
                      />
                    </IconButton>
                  </CustomToolTip>
                )}
                <ExportButton
                  sx={{
                    border: "1px solid " + colors.grey_48percent,
                  }}
                  handleExport={handleTableExport}
                />
              </Grid>
            </Grid>
            <Box
              display={"flex"}
              flexDirection={"column"}
              gap={2}
              justifyContent={"space-around"}
            >
              <VulnerabilitiesTable
                dataIsLoading={dataIsLoading}
                vulnerabilities={filteredVulnerabilities}
                assets={assets}
                controls={controls}
                gridRef={gridRef}
                onRowClick={handleRowClick}
                topExposureDays={topExposureDays}
                vulnerabilitiesAreFetching={vulnerabilitiesAreFetching}
              />
            </Box>
          </Box>
          <VulnerabilityDrawer
            openDrawer={openVulnerabilitiesDrawer}
            selectedVulnerability={selectedVulnerability}
            onClose={handleOnClose}
            topExposureDays={topExposureDays}
          />
          <ExportVulnerabilitiesModal
            open={exportModalOpen}
            onClose={() => setExportModalOpen(false)}
            onExport={handleExportFindingsConfirm}
            isLoading={exportMutation.isPending}
            tenants={tenants}
            msspId={currentTenantId}
            platformSelectedTenantIds={selectedTenantIds}
          />
        </AppPage>
      )}
    </>
  );
};

export default VulnerabilitiesView;
